"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _styles = require("@mui/styles");
var _react = _interopRequireDefault(require("react"));
var _reactSimpleKeyboard = _interopRequireDefault(require("react-simple-keyboard"));
require("./TouchKeyboard.scss");
require("react-simple-keyboard/build/css/index.css");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Touch keyboard
 *
 * @package HOPS
 * @subpackage Components
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */class TouchKeyboard extends _react.default.PureComponent {
  /**
   * Keyboard reference
   * 
   * @type {ReactSimpleKeyboard}
   */
  ref = null;

  /**
   * State
   *
   * @type {Object}
   */
  state = {
    /**
     * Shift?
     *
     * @type {Boolean}
     */
    shift: false,
    /**
     * Caps lock?
     *
     * @type {Boolean}
     */
    capslock: false,
    /**
     * Numeric numberpad?
     *
     * @type {Boolean}
     */
    numeric: false
  };

  /**
   * Component mounted.
   *
   * We set the keyboard's initial internal value.
   *
   * @return {void}
   */
  componentDidMount() {
    this.setState({
      numeric: this.props.numeric
    });
    this.setValueFromProp();
    this.updateThemeCssVariables();
  }

  /**
   * Component updated.
   *
   * We may need to update the keyboard's internal value.
   *
   * @param {Object} prevProps
   * @return {void}
   */
  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      if (this.props.value !== this.ref?.getInput?.()) {
        this.setValueFromProp();
      }
    }
  }

  /**
   * Set the keyboard's internal value using our `value` prop.
   *
   * @return {void}
   */
  setValueFromProp() {
    this.ref?.setInput?.(this.props.value);
  }

  /**
   * Update our CSS variables using our Material theme.
   *
   * @return {void}
   */
  updateThemeCssVariables() {
    const vars = this.themeCssVariables;
    for (const v of Object.keys(vars)) {
      this.ref?.keyboardDOM?.style?.setProperty?.(`--${v}`, vars[v]);
    }
  }

  /**
   * Value changed.
   *
   * @param {String} v
   * @return {void}
   */
  handleChange = v => {
    this.props.onChange(v, this.ref?.getCaretPosition());
  };

  /**
   * Handle a keyboard key press.
   * 
   * @param {String} btn `ReactSimpleKeyboard` key name
   * @return {void}
   */
  handleKeyPress = btn => {
    /**
     * Caps lock + shifting
     */
    if (btn === this.constructor._keys.shift && !this.state.capslock) {
      this.setState({
        shift: !this.state.shift
      });
    } else if (btn === this.constructor._keys.lock) {
      this.setState({
        shift: false,
        capslock: !this.state.capslock
      });
    } else if (this.state.shift) {
      this.setState({
        shift: false
      });
    }

    /**
     * Enter key
     */
    if (btn === this.constructor._keys.enter) {
      this.props.onEnter?.();
    }
  };

  /**
   * Set our keyboard reference pointer to a given reference.
   * 
   * @param {ReactSimpleKeyboard} ref
   * @return {void}
   */
  updateKeyboardRef = ref => {
    this.ref = ref;
    this.updateThemeCssVariables();
    if (this.props.value) {
      this.setValueFromProp();
      this.props.onChange(this.props.value, -1); // reset caret to end
    }
  };

  /**
   * Render.
   *
   * @return {ReactNode}
   */
  render() {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactSimpleKeyboard.default, {
      baseClass: this.props.inline ? "react-simple-keyboard-inline" : "react-simple-keyboard",
      display: this.constructor.display,
      keyboardRef: this.updateKeyboardRef,
      inputPattern: this.props.inputPattern,
      layout: this.constructor.layout,
      layoutName: this.layoutName,
      maxLength: this.props.maxLength,
      mergeDisplay: true,
      onChange: this.handleChange,
      onKeyPress: this.handleKeyPress,
      preventMouseDownDefault: true
    });
  }

  /**
   * Get the layout name to use.
   * 
   * @return {String}
   */
  get layoutName() {
    const layouts = this.constructor._layouts;
    if (!this.numeric) {
      return !this.shifted ? layouts.default : layouts.shift;
    } else {
      return layouts.numeric;
    }
  }

  /**
   * Get whether to attach to bottom of screen
   * 
   * @return {Boolean}
   */
  get inline() {
    return this.state.inline;
  }

  /**
   * Get whether to display the numeric keyboard layout.
   * 
   * @return {Boolean}
   */
  get numeric() {
    return this.state.numeric;
  }

  /**
   * Get whether to display the shifted keyboard layout.
   * 
   * @return {Boolean}
   */
  get shifted() {
    return this.state.shift || this.state.capslock;
  }

  /**
   * Get our current CSS variable values from our Material theme.
   * 
   * @return {Object}
   */
  get themeCssVariables() {
    const theme = this.props.theme;
    return {
      "tk-background-color": theme.palette.grey[300],
      "tk-button-color": theme.palette.background.default,
      "tk-button-border-color": theme.palette.divider,
      "tk-button-focus-color": theme.palette.action.hover,
      "tk-button-select-color": theme.palette.action.selected,
      "tk-button-text-color": theme.palette.text.primary,
      "tk-font-size": this.props.fontSize || "1em"
    };
  }

  /**
   * `ReactSimpleKeyboard` key definitions
   * 
   * @type {Object}
   */
  static _keys = {
    bksp: "{bksp}",
    enter: "{enter}",
    lock: "{lock}",
    shift: "{shift}",
    space: "{space}"
  };

  /**
   * `ReactSimpleKeyboard` layout definitions
   *
   * @type {Object}
   */
  static _layouts = {
    default: "default",
    shift: "shift",
    numeric: "numeric"
  };

  /**
   * `ReactSimpleKeyboard` `display` definition.
   * 
   * @type {Object}
   */
  static display = {
    [this._keys.enter]: "⏎",
    "{shift}": "⇧",
    "{shiftactivated}": "⇧",
    "{bksp}": "⌫"
  };

  /**
   * `ReactSimpleKeyboard` `layout` definition.
   *
   * @type {Object}
   */
  static layout = {
    [this._layouts.default]: [`\` 1 2 3 4 5 6 7 8 9 0 - = ${this._keys.bksp}`, `\\ q w e r t y u i o p [ ]`, `${this._keys.lock} a s d f g h j k l ; ' # ${this._keys.enter}`, `${this._keys.shift} z x c v b n m , . / ${this._keys.shift}`, `${this._keys.space}`],
    [this._layouts.shift]: [`¬ ! " £ $ % ^ & * ( ) _ + ${this._keys.bksp}`, `| Q W E R T Y U I O P { }`, `${this._keys.lock} A S D F G H J K L : @ ~ ${this._keys.enter}`, `${this._keys.shift} Z X C V B N M < > ? ${this._keys.shift}`, `${this._keys.space}`],
    [this._layouts.numeric]: [`7 8 9`, `4 5 6`, `1 2 3`, `0 00 ${this._keys.bksp}`]
  };
}
var _default = exports.default = (0, _styles.withTheme)(TouchKeyboard);